import { supabase } from '@/integrations/supabase/client';
import type { UserReputation, FeedbackTag, CreateRatingParams } from '@/types/reputation';
import { buildPublicProfileMap } from '@/services/profileUtils';

/**
 * Buscar reputação de um usuário
 */
export async function fetchUserReputation(userId: string): Promise<UserReputation | null> {
  const { data, error } = await supabase
    .from('user_reputation')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching user reputation:', error);
    return null;
  }

  // Cérebro's marketplace view is authoritative; legacy profile boosts are not reused.
  if (data) {
    return {
      ...data,
      reputation_score: 0,
      boost_plan_total: 0,
      boost_performance_total: 0,
    } as UserReputation;
  }

  return null;
}

/**
 * Buscar reputação de múltiplos usuários (para listings)
 */
export async function fetchUsersReputation(userIds: string[]): Promise<Record<string, UserReputation>> {
  if (userIds.length === 0) return {};

  const { data, error } = await supabase
    .from('user_reputation')
    .select('*')
    .in('user_id', userIds);

  if (error) {
    console.error('Error fetching users reputation:', error);
    return {};
  }

  const result: Record<string, UserReputation> = {};
  data?.forEach(rep => {
    result[rep.user_id] = rep as UserReputation;
  });

  return result;
}

/**
 * Buscar tags de feedback disponíveis
 */
export async function fetchFeedbackTags(role: 'buyer_to_seller' | 'seller_to_buyer'): Promise<FeedbackTag[]> {
  const { data, error } = await supabase
    .from('marketplace_feedback_tags')
    .select('*')
    .or(`applies_to.eq.${role},applies_to.eq.both`)
    .eq('is_enabled', true)
    .order('display_order', { ascending: true });

  if (error) {
    console.error('Error fetching feedback tags:', error);
    return [];
  }

  return data as FeedbackTag[];
}

/**
 * Criar uma avaliação
 */
export async function createRating(params: CreateRatingParams): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Usuário não autenticado');

  const { error } = await supabase
    .from('marketplace_ratings')
    .insert({
      negotiation_id: params.negotiation_id,
      rater_id: user.id,
      rated_user_id: params.rated_user_id,
      rating: params.rating,
      comment: params.comment || null,
      feedback_tags: params.feedback_tags || [],
      rating_role: params.rating_role,
      is_public: params.is_public ?? false, // Usuário pode escolher tornar público
    });

  if (error) throw error;
}

/**
 * Verificar se usuário já avaliou uma negociação
 */
export async function hasUserRated(negotiationId: string): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  const { data, error } = await supabase.rpc('has_user_rated_negotiation', {
    p_negotiation_id: negotiationId,
    p_user_id: user.id
  });

  if (error) {
    console.error('Error checking if user rated:', error);
    return false;
  }

  return data || false;
}

// fetchPendingRatings — REMOVED (dead code)
// The optimized RPC version is in src/services/pendingRatings.ts

/**
 * Buscar estatísticas de reputação para admin
 */
export async function fetchReputationStats(): Promise<{
  lowRepUsers: Array<UserReputation & { display_name: string }>;
  highCancellationUsers: Array<UserReputation & { display_name: string }>;
}> {
  // Buscar usuários com reputação baixa (< 3.0)
  const { data: lowRep } = await supabase
    .from('user_reputation')
    .select('*')
    .lt('average_rating', 3.0)
    .order('average_rating', { ascending: true })
    .limit(20);

  // Buscar usuários com muitos cancelamentos
  const { data: highCancel } = await supabase
    .from('user_reputation')
    .select('*')
    .gt('cancelled_by_user', 2)
    .order('cancelled_by_user', { ascending: false })
    .limit(20);

  // Enriquecer com nomes
  const enrichWithNames = async (users: any[]) => {
    if (!users || users.length === 0) return [];
    
    const userIds = users.map(u => u.user_id);
    const profileMapObj = await buildPublicProfileMap(userIds);
    
    return users.map(u => ({
      ...u,
      display_name: profileMapObj[u.user_id]?.display_name || 'Usuário'
    }));
  };

  return {
    lowRepUsers: await enrichWithNames(lowRep || []),
    highCancellationUsers: await enrichWithNames(highCancel || []),
  };
}

/**
 * Buscar feedbacks públicos recebidos por um usuário
 */
export interface PublicFeedback {
  id: string;
  rating: number;
  comment: string | null;
  created_at: string;
  rater_name: string;
  rater_avatar: string | null;
  rating_role: 'buyer_to_seller' | 'seller_to_buyer';
}

export async function fetchPublicFeedbacksReceived(
  userId: string,
  role?: 'as_buyer' | 'as_seller',
  limit: number = 5
): Promise<PublicFeedback[]> {
  let query = supabase
    .from('marketplace_ratings')
    .select('id, rating, comment, created_at, rater_id, rating_role')
    .eq('rated_user_id', userId)
    .eq('is_public', true)
    .order('created_at', { ascending: false })
    .limit(limit);

  // Filter by role if specified
  if (role === 'as_buyer') {
    // When viewing as buyer, show ratings received FROM sellers (seller_to_buyer)
    query = query.eq('rating_role', 'seller_to_buyer');
  } else if (role === 'as_seller') {
    // When viewing as seller, show ratings received FROM buyers (buyer_to_seller)
    query = query.eq('rating_role', 'buyer_to_seller');
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching public feedbacks:', error);
    return [];
  }

  if (!data || data.length === 0) return [];

  // Enrich with rater names
  const raterIds = [...new Set(data.map(r => r.rater_id))];
  const profileMapObj = await buildPublicProfileMap(raterIds);

  return data.map(r => ({
    id: r.id,
    rating: r.rating,
    comment: r.comment,
    created_at: r.created_at,
    rater_name: profileMapObj[r.rater_id]?.display_name || 'Usuário',
    rater_avatar: profileMapObj[r.rater_id]?.avatar_url || null,
    rating_role: r.rating_role as 'buyer_to_seller' | 'seller_to_buyer',
  }));
}
